import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AnalyticsService from '../../services/analyticsService';
import ROIOverview from './components/ROIOverview';
import PriceTrendsChart from './components/PriceTrendsChart';
import CollectionComparison from './components/CollectionComparison';
import TopPerformers from './components/TopPerformers';
import AIRecommendations from './components/AIRecommendations';

const AdvancedDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('1Y');
  const [exporting, setExporting] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    portfolioAnalytics: null,
    trends: [],
    comparison: [],
    topPerformers: []
  });

  useEffect(() => {
    if (user && !authLoading) {
      loadDashboardData();
    } else if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, timeRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        analyticsResult,
        trendsResult,
        comparisonResult,
        performersResult
      ] = await Promise.all([
        AnalyticsService?.getPortfolioAnalytics(user?.id),
        AnalyticsService?.getPortfolioTrends(user?.id, timeRange),
        AnalyticsService?.getCollectionComparison(user?.id),
        AnalyticsService?.getTopPerformers(user?.id, 10)
      ]);

      if (analyticsResult?.error) {
        throw analyticsResult?.error;
      }

      setDashboardData({
        portfolioAnalytics: analyticsResult?.data,
        trends: trendsResult?.data || [],
        comparison: comparisonResult?.data || [],
        topPerformers: performersResult?.data || []
      });

    } catch (err) {
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const result = await AnalyticsService?.exportPortfolioCSV(user?.id);
      
      if (result?.error) {
        throw result?.error;
      }

      const blob = new Blob([result?.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `portfolio_report_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);

    } catch (err) {
      setError(err?.message || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleCreateSnapshot = async () => {
    try {
      await AnalyticsService?.createPortfolioSnapshot(user?.id);
      await loadDashboardData();
    } catch (err) {
      setError(err?.message || 'Failed to create snapshot');
    }
  };

  const timeRangeOptions = [
    { label: '1M', value: '1M' },
    { label: '3M', value: '3M' },
    { label: '6M', value: '6M' },
    { label: '1Y', value: '1Y' },
    { label: 'All', value: 'All' }
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 lg:p-8">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={32} className="text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Failed to Load Analytics
              </h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={loadDashboardData}>
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1 sm:mb-2">Advanced Analytics</h1>
                <p className="text-muted-foreground text-sm sm:text-base">
                  Track ROI, analyze price trends, and compare portfolio performance.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {/* Time Range Selector */}
                <div className="flex bg-muted/30 rounded-lg p-1 overflow-x-auto scrollbar-hide">
                  {timeRangeOptions?.map((option) => (
                    <button
                      key={option?.value}
                      onClick={() => setTimeRange(option?.value)}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap min-h-[40px] ${
                        timeRange === option?.value
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-card-foreground'
                      }`}
                    >
                      {option?.label}
                    </button>
                  ))}
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Export Button */}
                  <Button
                    onClick={handleExportCSV}
                    loading={exporting}
                    iconName="Download"
                    variant="outline"
                    size="md"
                  >
                    Export
                  </Button>
                  
                  {/* Refresh Button */}
                  <Button
                    onClick={loadDashboardData}
                    iconName="RefreshCw"
                    variant="default"
                    size="md"
                  >
                    Refresh
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* ROI Overview */}
          <ROIOverview analytics={dashboardData?.portfolioAnalytics} />

          {/* AI-Powered Recommendations */}
          <AIRecommendations 
            portfolioData={dashboardData?.portfolioAnalytics}
            trends={dashboardData?.trends}
            collections={dashboardData?.comparison}
            topPerformers={dashboardData?.topPerformers}
          />

          {/* Price Trends Chart */}
          <PriceTrendsChart 
            data={dashboardData?.trends} 
            timeRange={timeRange}
          />

          {/* Collection Comparison & Top Performers */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <CollectionComparison data={dashboardData?.comparison} />
            <TopPerformers data={dashboardData?.topPerformers} />
          </div>

          {/* Additional Actions */}
          <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-card-foreground mb-4">Portfolio Management</h3>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleCreateSnapshot}
                iconName="Camera"
                variant="outline"
                size="md"
              >
                Create Snapshot
              </Button>
              <Button
                onClick={() => window.location.href = '/collection-dashboard'}
                iconName="LayoutDashboard"
                variant="outline"
                size="md"
              >
                View Collections
              </Button>
              <Button
                onClick={() => window.location.href = '/market-analysis'}
                iconName="TrendingUp"
                variant="outline"
                size="md"
              >
                Market Analysis
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdvancedDashboard;